import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll position on ALL scrollable elements in the page.
 *
 * Why this is necessary:
 * Each page wrapper (.homepage, .treasury-page, .stake-page, .buy-page, etc.)
 * has `overflow-y: auto` in its CSS, making it an independent scroll container.
 * Simply resetting `window` / `document.body` does nothing because those
 * elements are NOT the ones that are actually scrolled — the page-level
 * div is. When React swaps the route content, the new page's wrapper inherits
 * the scroll offset from the previous page's wrapper because they both live
 * inside the same `.app` parent.
 *
 * The fix: walk the DOM starting from #root and reset every element whose
 * computed overflow allows scrolling (auto / scroll / overlay).
 */
const SCROLLABLE_OVERFLOW = new Set(['auto', 'scroll', 'overlay']);

const resetAllScrollContainers = () => {
  // 1. Always reset the top-level targets
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  // 2. Walk every element inside #root and reset anything scrollable
  const root = document.getElementById('root') || document.body;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.currentNode;

  while (node) {
    if (node.scrollTop !== 0 || node.scrollLeft !== 0) {
      // This element is actually scrolled — reset it immediately
      node.scrollTop = 0;
      node.scrollLeft = 0;
    } else if (node instanceof HTMLElement) {
      // Even if not currently scrolled, check if it CAN scroll
      // (the new page may have inherited scroll offset from layout)
      const style = window.getComputedStyle(node);
      if (
        SCROLLABLE_OVERFLOW.has(style.overflowY) ||
        SCROLLABLE_OVERFLOW.has(style.overflowX)
      ) {
        node.scrollTop = 0;
        node.scrollLeft = 0;
      }
    }
    node = walker.nextNode();
  }
};

/**
 * ScrollToTop component — scrolls to top when navigating to a new page.
 * Does NOT scroll when only the hash fragment changes (e.g. '/treasury#vault-holdings').
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  // Disable browser's native scroll restoration
  useLayoutEffect(() => {
    if (!window.history || typeof window.history.scrollRestoration !== 'string') {
      return undefined;
    }

    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  useLayoutEffect(() => {
    // Hash navigation → scroll to the target element instead
    if (hash) {
      const frameId = window.requestAnimationFrame(() => {
        const id = decodeURIComponent(hash.substring(1));
        const element = document.getElementById(id);

        if (element) {
          element.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }

        resetAllScrollContainers();
      });

      return () => window.cancelAnimationFrame(frameId);
    }

    // Immediate reset (catches most cases)
    resetAllScrollContainers();

    // Double-rAF: fires after the browser paints the new route
    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        resetAllScrollContainers();
      });
    });

    // Fallback timeout for lazy-loaded pages
    const timerId = setTimeout(() => {
      resetAllScrollContainers();
    }, 80);

    return () => {
      window.cancelAnimationFrame(frameId);
      clearTimeout(timerId);
    };
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
